import React, { useState, useEffect } from 'react';
import './VendorDashboard.css';
import DataSourceIndicator from './DataSourceIndicator';

interface VendorAnalytics {
  summary: {
    totalVendors: number;
    activeVendors: number;
    avgRating: number;
    integratedVendors: number;
  };
  topVendors: Array<{
    id: string;
    name: string;
    rating: number;
    qualityRating: number;
    deliveryRating: number;
    serviceRating: number;
  }>;
  vendorByType: Array<{
    vendorType: string;
    count: number;
  }>;
}

interface ExpiringContract {
  id: string;
  contractNumber: string;
  supplier: {
    name: string;
  };
  endDate: string;
  contractStatus: string;
  contractValue?: number;
}

interface ExpiringCertification {
  id: string;
  certificationName: string;
  supplier: {
    name: string;
  };
  expirationDate: string;
  certificationType: string;
}

interface VendorDashboardProps {
  enterpriseId: string;
}

export const VendorDashboard: React.FC<VendorDashboardProps> = ({ enterpriseId }) => {
  const [analytics, setAnalytics] = useState<VendorAnalytics | null>(null);
  const [expiringContracts, setExpiringContracts] = useState<ExpiringContract[]>([]);
  const [expiringCertifications, setExpiringCertifications] = useState<ExpiringCertification[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
  }, [enterpriseId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load analytics, expiring contracts, and certifications in parallel
      const [analyticsRes, contractsRes, certificationsRes] = await Promise.all([
        fetch(`/api/vendors/${enterpriseId}/analytics`),
        fetch(`/api/vendors/${enterpriseId}/contracts/expiring?daysAhead=90`),
        fetch(`/api/vendors/${enterpriseId}/certifications/expiring?daysAhead=60`)
      ]);

      const analyticsData = await analyticsRes.json();
      const contractsData = await contractsRes.json();
      const certificationsData = await certificationsRes.json();

      if (analyticsData.success !== false) {
        setAnalytics(analyticsData.data);
        setDataSource(analyticsData.dataSource);
      }

      if (contractsData.success !== false) {
        setExpiringContracts(contractsData.data || []);
      }

      if (certificationsData.success !== false) {
        setExpiringCertifications(certificationsData.data || []);
      }
    } catch (error) {
      console.error('Error loading vendor dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysUntilExpiration = (dateString: string) => {
    const today = new Date();
    const expirationDate = new Date(dateString);
    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return '#10b981';
    if (rating >= 4.0) return '#f59e0b';
    if (rating >= 3.5) return '#f97316';
    return '#ef4444';
  };

  const getExpirationUrgency = (days: number) => {
    if (days <= 7) return 'critical';
    if (days <= 30) return 'warning';
    return 'normal';
  };

  if (loading) {
    return (
      <div className="vendor-dashboard">
        <div className="loading-spinner">Loading vendor dashboard...</div>
      </div>
    );
  }

  return (
    <div className="vendor-dashboard">
      <div className="dashboard-header">
        <h2>Vendor Management Dashboard</h2>
        {dataSource && <DataSourceIndicator dataSource={dataSource} />}
      </div>

      {analytics && (
        <div className="dashboard-content">
          {/* Summary Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-value">{analytics.summary.totalVendors}</div>
                <div className="stat-label">Total Vendors</div>
                <div className="stat-subtitle">
                  {analytics.summary.activeVendors} active
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-value" style={{ color: getRatingColor(analytics.summary.avgRating) }}>
                  {analytics.summary.avgRating.toFixed(1)}
                </div>
                <div className="stat-label">Average Rating</div>
                <div className="stat-subtitle">Vendor performance</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-value">{analytics.summary.integratedVendors}</div>
                <div className="stat-label">API Integrated</div>
                <div className="stat-subtitle">
                  {Math.round((analytics.summary.integratedVendors / analytics.summary.totalVendors) * 100)}% connected
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-content">
                <div className="stat-value">{expiringContracts.length + expiringCertifications.length}</div>
                <div className="stat-label">Expiring Soon</div>
                <div className="stat-subtitle">
                  {expiringContracts.length} contracts, {expiringCertifications.length} certs
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="dashboard-grid">
            {/* Top Performing Vendors */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>Top Performing Vendors</h3>
                <span className="card-subtitle">Ranked by overall rating</span>
              </div>
              <div className="vendor-rankings">
                {analytics.topVendors.slice(0, 10).map((vendor, index) => (
                  <div key={vendor.id} className="vendor-rank-item">
                    <div className="rank-number">#{index + 1}</div>
                    <div className="vendor-info">
                      <div className="vendor-name">{vendor.name}</div>
                      <div className="vendor-ratings">
                        <span className="overall-rating" style={{ color: getRatingColor(vendor.rating) }}>
                          Rating: {vendor.rating.toFixed(1)}
                        </span>
                        <div className="rating-breakdown">
                          <span title="Quality">Q: {vendor.qualityRating.toFixed(1)}</span>
                          <span title="Delivery">D: {vendor.deliveryRating.toFixed(1)}</span>
                          <span title="Service">S: {vendor.serviceRating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Vendor Distribution */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>Vendor Distribution</h3>
                <span className="card-subtitle">By vendor type</span>
              </div>
              <div className="vendor-distribution">
                {analytics.vendorByType.map(item => (
                  <div key={item.vendorType} className="distribution-item">
                    <div className="distribution-label">
                      {item.vendorType.charAt(0).toUpperCase() + item.vendorType.slice(1)}
                    </div>
                    <div className="distribution-bar">
                      <div 
                        className="distribution-fill"
                        style={{ 
                          width: `${(item.count / analytics.summary.totalVendors) * 100}%`,
                          backgroundColor: getVendorTypeColor(item.vendorType)
                        }}
                      ></div>
                    </div>
                    <div className="distribution-count">{item.count}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expiring Contracts */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>Expiring Contracts</h3>
                <span className="card-subtitle">Next 90 days</span>
              </div>
              <div className="expiring-items">
                {expiringContracts.length === 0 ? (
                  <div className="empty-state">No contracts expiring soon</div>
                ) : (
                  expiringContracts.slice(0, 8).map(contract => {
                    const daysLeft = getDaysUntilExpiration(contract.endDate);
                    const urgency = getExpirationUrgency(daysLeft);
                    
                    return (
                      <div key={contract.id} className={`expiring-item ${urgency}`}>
                        <div className="item-info">
                          <div className="item-title">{contract.contractNumber}</div>
                          <div className="item-vendor">{contract.supplier.name}</div>
                        </div>
                        <div className="item-expiration">
                          <div className="days-left">{daysLeft} days</div>
                          <div className="expiration-date">{formatDate(contract.endDate)}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Expiring Certifications */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>Expiring Certifications</h3>
                <span className="card-subtitle">Next 60 days</span>
              </div>
              <div className="expiring-items">
                {expiringCertifications.length === 0 ? (
                  <div className="empty-state">No certifications expiring soon</div>
                ) : (
                  expiringCertifications.slice(0, 8).map(cert => {
                    const daysLeft = getDaysUntilExpiration(cert.expirationDate);
                    const urgency = getExpirationUrgency(daysLeft);
                    
                    return (
                      <div key={cert.id} className={`expiring-item ${urgency}`}>
                        <div className="item-info">
                          <div className="item-title">{cert.certificationName}</div>
                          <div className="item-vendor">{cert.supplier.name}</div>
                        </div>
                        <div className="item-expiration">
                          <div className="days-left">{daysLeft} days</div>
                          <div className="expiration-date">{formatDate(cert.expirationDate)}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function getVendorTypeColor(vendorType: string): string {
  const colors: { [key: string]: string } = {
    distributor: '#3b82f6',
    manufacturer: '#10b981',
    supplier: '#f59e0b',
    service: '#8b5cf6',
    broker: '#ef4444'
  };
  return colors[vendorType] || '#6b7280';
}